import { v4 as uuidv4 } from "uuid";
import { makeAutoObservable, reaction, action, untracked } from "mobx";

import Exercise from "./Exercise";
import Serie from "./Serie";
import Attribute from "./Attribute";
import { ProtocolTitles } from "../../Resources/Constants";
import { validateWorkout } from "../utils/firestoreValidation";
import { showDevToast } from "../utils/devToast";

class Workout {
  exercisesToAdd = [];
  showingEditWorkout = false;
  auxDate = null;
  showingCoachWorkouts = false;

  // UI-only (not persisted)
  exerciseFocusRequest = null; // { exerciseId, field, serieId }

  constructor(
    id = uuidv4(),
    date = null,
    name = "",
    order = 0,
    week = 1,
    isSelected = false,
    block = null,
    exercises = [],
    coach = null,
    notes = ""
  ) {
    this.id = id;
    this.date = date;
    this.name = name;
    this.order = order;
    this.week = week;
    this.exercises = exercises;
    this.isSelected = isSelected;
    this.block = block;
    this.exercises = exercises;
    this.coach = coach;
    this.notes = notes;
    this.hasChanged = false;

    makeAutoObservable(this);

    reaction(
      () => ({
        id: this.id,
        date: this.date,
        name: this.name,
        notes: this.notes,
        order: this.order,
        week: this.week,
        exercises: this.exercises,
      }),
      () => {
        console.log("Reaction Workout");

        if (this.block?.client && !this.block.client.avoidTransaction) {
          this.hasChanged = true;
          this.block.client.updateClientTransactionData(this.block.id);
          this.block.client.startOrResetBatchTimer();
        } else if (this.block?.coach) {
          this.block.coach.startOrResetBatchTimer();
          this.block.coach.updateCoachTransactionBlocks(
            this.block.id.toLowerCase()
          );
        } else if (this.coach) {
          this.hasChanged = true;
          this.coach.updateCoachTransactionWorkouts(this.id);
          this.coach.startOrResetBatchTimer();
        }
      }
    );
  }

  // #region /* WORKOUT LIBRARY */
  copyToLibrary(showToast) {
    const coach = this.block?.client?.coach ?? this.block?.coach;
    if (coach) {
      const workoutLibrary = coach.workoutLibrary ?? [];
      const newOrder =
        workoutLibrary.length > 0
          ? Math.max(...workoutLibrary.map((w) => w.order)) + 1
          : 1;
      const newWorkout = new Workout(
        uuidv4(),
        null,
        this.name,
        newOrder,
        0,
        false,
        null,
        [],
        coach
      );
      this.exercises &&
        this.exercises.forEach((exercise) => {
          const newExercise = new Exercise(
            uuidv4(),
            exercise.name,
            exercise.originalName,
            exercise.notes,
            exercise.order,
            true,
            false,
            newWorkout,
            [],
            [],
            undefined,
            undefined,
            exercise.link
          );
          exercise.series &&
            exercise.series.forEach((serie) => {
              newExercise.copySerie(serie);
            });
          exercise.attributes &&
            exercise.attributes.forEach((attribute) => {
              newExercise.copyAttribute(attribute);
            });
          newWorkout.exercises.push(newExercise);
        });
      if (!coach.workoutLibrary) {
        coach.workoutLibrary = [];
      }
      coach.workoutLibrary.push(newWorkout);
      if (newWorkout.coach) {
        newWorkout.coach.updateCoachTransactionWorkouts(newWorkout.id);
        newWorkout.coach.startOrResetBatchTimer();
      }

      showToast("workoutExportedToLibrary-string", {
        type: "info",
        description: "workoutExportedToLibraryDescription-string",
        variables: {
          workoutName: this.name,
        },
      });
    }
  }

  addLibraryWorkout(workout) {
    //FUNC TO ADD THE EXERCISES FROM A WORKOUT OF THE LIBRARY TO ANOTHER WORKOUT
    //THIS IS A WORKOUT FROM THE LIBRARY
    this.exercises
      .sort((a, b) => a.order - b.order)
      .forEach((exercise) => {
        const newOrder =
          workout.exercisesToAdd.length > 0
            ? Math.max(
                ...workout.exercisesToAdd.map((exercise) => exercise.order)
              ) + 1
            : 0;
        const newExercise = new Exercise(
          uuidv4(),
          exercise.name,
          exercise.originalName,
          exercise.notes,
          newOrder,
          true,
          false,
          workout,
          [],
          [],
          uuidv4(),
          undefined,
          exercise.link
        );
        exercise.series &&
          exercise.series.forEach((serie) => {
            const newSerie = new Serie(
              uuidv4(),
              serie.order,
              serie.reps,
              serie.rpe,
              serie.weight,
              serie.targetReps,
              serie.targetRpe,
              serie.targetWeight,
              newExercise,
              serie.relevance,
              undefined,
              serie.requireVideo
            );
            newExercise.series.push(newSerie);
          });
        exercise.attributes &&
          exercise.attributes.forEach((attribute) => {
            const newAttribute = new Attribute(
              uuidv4(),
              attribute.name,
              attribute.title,
              attribute.subtitle,
              attribute.order,
              false,
              attribute.isSelected,
              null,
              newExercise
            );
            newExercise.attributes.push(newAttribute);
          });
        workout.exercisesToAdd.push(newExercise);
      });
  }
  // #endregion

  // #region /* ADD EXERCISES */
  get selectedExerciseToAdd() {
    return this.exercisesToAdd.find((exercise) => exercise.isSelected === true);
  }
  setExercisesToAdd(newArray) {
    if (newArray) {
      // this.exercisesToAdd = newArray;
      this.exercisesToAdd = [...newArray];
    } else {
      this.exercisesToAdd = [];
      this.exercises.map((exercise) => exercise.duplicateExercise());
    }
  }

  setExercises() {
    // Identify exercises that were deleted
    const exercisesToAddIds = this.exercisesToAdd.map((exercise) =>
      exercise.exerciseToAddId.toString()
    );
    // console.log("exercisesToAddIds: ", exercisesToAddIds);
    const exercisesToDelete = this.exercises.filter(
      (exercise) => !exercisesToAddIds.includes(exercise.id.toString())
    );

    // Before deleting the exercises, call copyExerciseToBlockProtocols for each
    exercisesToDelete.forEach((e) => e.copyExerciseToBlockProtocols());

    this.exercises = this.exercises.filter(
      (exercise) => !exercisesToDelete.includes(exercise)
    );

    this.exercisesToAdd.forEach((exerciseToAdd) => {
      exerciseToAdd.duplicateExerciseToAdd(this);
    });

    // After changes are applied, you might want to clear exercisesToAdd or repopulate it for further edits
    this.exercisesToAdd = [];

    // Continue with your existing logic to update client data and reset timer
    if (this.block && this.block.client) {
      this.block.client.updateClientTransactionData(this.block.id);
      this.block.client.startOrResetBatchTimer();
    }
  }

  setSelectedExercise(selectedExercise) {
    this.exercises.forEach((exercise) => {
      if (exercise.id === selectedExercise.id) {
        exercise.setIsSelected(true);
      } else {
        exercise.setIsSelected(false);
      }
    });
  }

  setSelectedExerciseToAdd(selectedExercise) {
    this.exercisesToAdd.forEach((exercise) => {
      if (exercise.id === selectedExercise.id) {
        exercise.setIsSelected(true);
      } else {
        exercise.setIsSelected(false);
      }
    });
  }

  moveExercise(exercise, direction) {
    // First, sort the exercises by their order to ensure they are in the correct initial order
    const exercisesArray = this.exercisesToAdd.sort(
      (a, b) => a.order - b.order
    );

    // Find the index of the exercise in the sorted array
    const index = exercisesArray.findIndex((ex) => ex.id === exercise.id);
    if (index === -1) return; // Exit if the exercise is not found

    // Determine the new index based on the direction ('up' moves the item earlier, 'down' moves it later)
    const newIndex = direction === "up" ? index - 1 : index + 1;

    // Check if the new index is within bounds
    if (newIndex >= 0 && newIndex < exercisesArray.length) {
      // Swap the exercises in the array
      [exercisesArray[index], exercisesArray[newIndex]] = [
        exercisesArray[newIndex],
        exercisesArray[index],
      ];

      // Update the order of the exercises based on their new positions
      exercisesArray.forEach((ex, idx) => {
        ex.order = idx;
      });
    }
  }

  /**
   * Reorder an exercise within this workout (drag-and-drop)
   * @param {number} fromIndex - Current index in sorted exercises array
   * @param {number} toIndex - Target index in sorted exercises array
   */
  reorderExercise(fromIndex, toIndex) {
    if (fromIndex === toIndex) return;
    if (fromIndex < 0 || toIndex < 0) return;
    if (fromIndex >= this.exercises.length || toIndex >= this.exercises.length)
      return;

    // Sort by order first to ensure correct indices
    const sortedExercises = this.exercises
      .slice()
      .sort((a, b) => a.order - b.order);

    // Remove the item from its original position
    const [movedItem] = sortedExercises.splice(fromIndex, 1);

    // Insert at the new position
    sortedExercises.splice(toIndex, 0, movedItem);

    // Update order property for all items
    sortedExercises.forEach((exercise, idx) => {
      exercise.order = idx;
    });

    // Update the array reference to trigger MobX reactivity
    this.exercises = sortedExercises;

    this.setProperty("hasChanged", true);
  }

  /**
   * Get exercises sorted by order.
   * @returns {Exercise[]}
   */
  getSortedExercises() {
    return this.exercises.slice().sort((a, b) => a.order - b.order);
  }

  /**
   * Returns the protocol pack for an exercise (all exercises sharing protocolSlot).
   * If no protocolSlot, returns the exercise as a single-item pack.
   */
  getProtocolPack(exercise) {
    if (!exercise) return [];
    const protocolSlot =
      typeof exercise.protocolSlot === "string" && exercise.protocolSlot.trim()
        ? exercise.protocolSlot
        : null;
    if (!protocolSlot) return [exercise];
    return this.getSortedExercises().filter(
      (ex) => ex?.protocolSlot === protocolSlot
    );
  }

  /**
   * Reorder a protocol pack as a contiguous group.
   * @param {string} exerciseId - ID of the dragged exercise
   * @param {number} targetIndex - Target index in the sorted exercises array
   */
  reorderExercisePack(exerciseId, targetIndex) {
    const sortedExercises = this.getSortedExercises();
    const movedExercise = sortedExercises.find((ex) => ex?.id === exerciseId);
    if (!movedExercise) return;

    const pack = this.getProtocolPack(movedExercise);
    if (pack.length <= 1) {
      const fromIndex = sortedExercises.indexOf(movedExercise);
      this.reorderExercise(fromIndex, targetIndex);
      return;
    }

    const packIds = new Set(pack.map((ex) => ex?.id));
    const packIndices = pack
      .map((ex) => sortedExercises.indexOf(ex))
      .filter((idx) => idx !== -1)
      .sort((a, b) => a - b);
    if (packIndices.length === 0) return;

    const packStart = packIndices[0];
    const packEnd = packIndices[packIndices.length - 1];
    if (targetIndex >= packStart && targetIndex <= packEnd) return;

    const remaining = sortedExercises.filter((ex) => !packIds.has(ex?.id));
    const removedBeforeTarget = packIndices.filter(
      (idx) => idx < targetIndex
    ).length;
    const insertIndex = Math.max(
      0,
      Math.min(targetIndex - removedBeforeTarget, remaining.length)
    );

    const nextExercises = remaining.slice();
    nextExercises.splice(insertIndex, 0, ...pack);
    nextExercises.forEach((exercise, idx) => {
      exercise.order = idx;
    });

    this.exercises = nextExercises;
    this.setProperty("hasChanged", true);
  }

  /**
   * Remove multiple exercises by ID and return them in sorted order.
   * @param {string[]} exerciseIds
   * @returns {Exercise[]}
   */
  removeExercisesByIds(exerciseIds = []) {
    const ids = new Set(exerciseIds);
    const sortedExercises = this.getSortedExercises();
    const removed = sortedExercises.filter((ex) => ids.has(ex?.id));
    const remaining = sortedExercises.filter((ex) => !ids.has(ex?.id));

    remaining.forEach((exercise, idx) => {
      exercise.order = idx;
    });
    this.exercises = remaining;
    this.setProperty("hasChanged", true);

    return removed;
  }

  /**
   * Receive a pack of exercises into this workout at target index.
   * @param {Exercise[]} exercises
   * @param {number} targetIndex
   */
  receiveExercisePack(exercises, targetIndex) {
    if (!Array.isArray(exercises) || exercises.length === 0) return;

    const sortedExercises = this.getSortedExercises();
    const insertIndex = Math.max(
      0,
      Math.min(targetIndex, sortedExercises.length)
    );

    exercises.forEach((exercise) => {
      if (exercise) {
        exercise.workout = this;
      }
    });

    const nextExercises = sortedExercises.slice();
    nextExercises.splice(insertIndex, 0, ...exercises);
    nextExercises.forEach((exercise, idx) => {
      exercise.order = idx;
    });

    this.exercises = nextExercises;
    this.setProperty("hasChanged", true);
  }

  /**
   * Add an exercise from another workout to this workout at a specific position
   * @param {Exercise} exercise - The exercise to add (will be transferred, not copied)
   * @param {number} targetIndex - Position to insert at (in sorted order)
   */
  receiveExercise(exercise, targetIndex) {
    if (!exercise) return;

    // Sort current exercises by order
    const sortedExercises = this.exercises
      .slice()
      .sort((a, b) => a.order - b.order);

    // Clamp target index
    const insertIndex = Math.max(
      0,
      Math.min(targetIndex, sortedExercises.length)
    );

    // Update exercise's workout reference
    exercise.workout = this;

    // Insert at position
    sortedExercises.splice(insertIndex, 0, exercise);

    // Update order for all exercises
    sortedExercises.forEach((ex, idx) => {
      ex.order = idx;
    });

    // Update the array
    this.exercises = sortedExercises;

    this.setProperty("hasChanged", true);
  }

  /**
   * Remove an exercise from this workout (for cross-workout moves)
   * @param {string} exerciseId - The ID of the exercise to remove
   * @returns {Exercise|null} - The removed exercise, or null if not found
   */
  removeExercise(exerciseId) {
    const index = this.exercises.findIndex((ex) => ex.id === exerciseId);
    if (index === -1) return null;

    const [removed] = this.exercises.splice(index, 1);

    // Re-order remaining exercises
    const sortedExercises = this.exercises
      .slice()
      .sort((a, b) => a.order - b.order);
    sortedExercises.forEach((ex, idx) => {
      ex.order = idx;
    });
    this.exercises = sortedExercises;

    this.setProperty("hasChanged", true);

    return removed;
  }

  // addNewSlotExerciseToWorkout(slotExercise) {
  //   this.copyExercise(slotExercise);
  // }
  // #endregion

  // #region /* EXERCISE ACTIONS */
  get exercisesShown() {
    return this.exercises.every((exercise) => exercise.isShown === true);
  }

  showOrFoldAllExercises() {
    if (this.exercisesShown) {
      this.exercises.forEach((exercise) => {
        exercise.isShown = false;
      });
    } else {
      this.exercises.forEach((exercise) => {
        exercise.isShown = true;
      });
    }
  }
  get hasProtocolSlotExercises() {
    return (
      Array.isArray(this.exercises) &&
      this.exercises.some((exercise) => exercise?.protocolSlot != null)
    );
  }
  get selectedExercise() {
    return this.exercises.find((exercise) => exercise.isSelected === true);
  }
  // #endregion

  // #region /* REAL-TIME & COPY */
  fetchExercise(exercise) {
    const newExercise = new Exercise(
      String(exercise.id),
      exercise.name,
      exercise.original_name,
      exercise.notes,
      exercise.order,
      undefined,
      false,
      this,
      [],
      [],
      undefined,
      undefined,
      exercise.link
    );
    if (exercise.order === 0) {
      newExercise.setProperty("isSelected", true);
    }

    newExercise.isSecondaryProtocol = exercise.is_secondary_protocol ?? false;

    newExercise.protocolText = exercise.protocol_text ?? ""; //Allow exercises to have a protocolText when they have been temporarily unlinked from slot

    if (exercise.protocol_slot !== undefined) {
      newExercise.protocolSlot = exercise.protocol_slot ?? null;
      newExercise.protocolWeek = exercise.protocol_week ?? null;
    } else {
      if (exercise.squat_slot !== undefined) {
        // newExercise.setProperty("squatSlot", exercise.squat_slot);
        if (exercise.squat_slot === 0) {
          newExercise.protocolSlot = ProtocolTitles.PRIMARY_SQUAT;
        } else if (exercise.squat_slot === 1) {
          newExercise.protocolSlot = ProtocolTitles.SECONDARY_SQUAT;
        } else if (exercise.squat_slot === 2) {
          newExercise.protocolSlot = ProtocolTitles.TERTIARY_SQUAT;
        } else if (exercise.squat_slot === 3) {
          newExercise.protocolSlot = ProtocolTitles.QUATERNARY_SQUAT;
        } else if (exercise.squat_slot === 4) {
          newExercise.protocolSlot = ProtocolTitles.QUINARY_SQUAT;
        }
      }
      if (exercise.bench_press_slot !== undefined) {
        // newExercise.setProperty("benchPressSlot", exercise.bench_press_slot);
        if (exercise.bench_press_slot === 0) {
          newExercise.protocolSlot = ProtocolTitles.PRIMARY_BENCH_PRESS;
        } else if (exercise.bench_press_slot === 1) {
          newExercise.protocolSlot = ProtocolTitles.SECONDARY_BENCH_PRESS;
        } else if (exercise.bench_press_slot === 2) {
          newExercise.protocolSlot = ProtocolTitles.TERTIARY_BENCH_PRESS;
        } else if (exercise.bench_press_slot === 3) {
          newExercise.protocolSlot = ProtocolTitles.QUATERNARY_BENCH_PRESS;
        } else if (exercise.bench_press_slot === 4) {
          newExercise.protocolSlot = ProtocolTitles.QUINARY_BENCH_PRESS;
        }
      }
      if (exercise.deadlift_slot !== undefined) {
        // newExercise.setProperty("deadliftSlot", exercise.deadlift_slot);
        if (exercise.deadlift_slot === 0) {
          newExercise.protocolSlot = ProtocolTitles.PRIMARY_DEADLIFT;
        } else if (exercise.deadlift_slot === 1) {
          newExercise.protocolSlot = ProtocolTitles.SECONDARY_DEADLIFT;
        } else if (exercise.deadlift_slot === 2) {
          newExercise.protocolSlot = ProtocolTitles.TERTIARY_DEADLIFT;
        } else if (exercise.deadlift_slot === 3) {
          newExercise.protocolSlot = ProtocolTitles.QUATERNARY_DEADLIFT;
        } else if (exercise.deadlift_slot === 4) {
          newExercise.protocolSlot = ProtocolTitles.QUINARY_DEADLIFT;
        }
      }
    }
    exercise?.series.forEach((serie) => {
      newExercise.fetchSerie(serie);
    });
    exercise?.attributes.forEach((attribute) => {
      newExercise.fetchAttribute(attribute);
    });
    this.exercises.push(newExercise);
  }

  updateWorkout(workout) {
    this.date = workout.date?.toDate() ?? null;
    this.name = workout.name ?? "";
    this.order = workout.order ?? 0;
    this.week = workout.week ?? 0;
  }

  uploadToCloud(timestamp = null) {
    let exercisesArray = this.exercises.map((exercise) =>
      exercise.uploadToCloud()
    );

    const data = {
      id: this.id.toString().toLowerCase(),
      date: this.date,
      name: this.name,
      order: this.order,
      week: this.week,
      exercises: exercisesArray,
      ...(timestamp && { last_update: timestamp }),
      ...(this.notes != "" && { notes: this.notes }),
      device_os: "web",
    };

    // Validate data (always runs, lightweight)
    // Only show toast in development
    if (process.env.NODE_ENV === "development") {
      const validation = validateWorkout(data);
      if (!validation.valid) {
        const errorMsg = validation.errors.join("; ");
        showDevToast("Workout validation failed", errorMsg);
      }
    }

    return data;
  }
  copyExercise(exercise, keepActual = false) {
    const newExercise = new Exercise(
      undefined,
      exercise.name,
      exercise.originalName,
      exercise.notes,
      exercise.order,
      undefined,
      false,
      this,
      [],
      [],
      undefined,
      undefined,
      exercise.link
    );
    if (exercise.order === 0) {
      newExercise.setProperty("isSelected", true);
    }

    newExercise.isSecondaryProtocol = exercise.isSecondaryProtocol ?? null;

    if (exercise.protocolSlot) {
      newExercise.protocolSlot = exercise.protocolSlot ?? null;
      newExercise.protocolWeek = exercise.protocolWeek ?? null;
      newExercise.protocolText = exercise.protocolText ?? "";
    }
    exercise?.series.forEach((serie) => {
      newExercise.copySerie(serie, keepActual);
    });
    exercise?.attributes.forEach((attribute) => {
      newExercise.copyAttribute(attribute);
    });
    this.exercises.push(newExercise);

    return newExercise;
  }

  pasteExercisesFromClipboardPayloads(payloads) {
    const list = Array.isArray(payloads) ? payloads : [];
    if (list.length === 0) return 0;

    const existing = Array.isArray(this?.exercises) ? this.exercises : [];
    let nextOrder =
      existing.length > 0
        ? Math.max(...existing.map((ex) => ex?.order ?? 0)) + 1
        : 0;

    let createdCount = 0;

    const normalizeProtocolSlot = (protocolSlot) => {
      if (typeof protocolSlot !== "string") return null;
      const trimmed = protocolSlot.trim();
      return trimmed ? trimmed : null;
    };

    const week = this?.week ?? null;
    const weekWorkouts = Array.isArray(this?.block?.workouts)
      ? this.block.workouts.filter((w) => w?.week === week)
      : [this];
    const weekSlotStatus = new Map();
    const recordWeekSlot = (slot, isSecondary) => {
      if (!slot) return;
      const existingStatus = weekSlotStatus.get(slot) || {
        hasPrimary: false,
        hasSecondary: false,
      };
      if (isSecondary) {
        existingStatus.hasSecondary = true;
      } else {
        existingStatus.hasPrimary = true;
      }
      weekSlotStatus.set(slot, existingStatus);
    };

    weekWorkouts.forEach((workout) => {
      const exercises = Array.isArray(workout?.exercises)
        ? workout.exercises
        : [];
      exercises.forEach((exercise) => {
        const slot = normalizeProtocolSlot(exercise?.protocolSlot);
        if (!slot) return;
        recordWeekSlot(slot, exercise?.isSecondaryProtocol === true);
      });
    });

    list.forEach((payload) => {
      if (!payload) return;

      const newExercise = new Exercise(
        uuidv4(),
        typeof payload?.name === "string" ? payload.name : "",
        typeof payload?.originalName === "string" ? payload.originalName : "",
        typeof payload?.notes === "string" ? payload.notes : "",
        nextOrder,
        true,
        false,
        this,
        [],
        [],
        "",
        null,
        typeof payload?.link === "string" ? payload.link : ""
      );
      nextOrder += 1;

      const protocolSlot = normalizeProtocolSlot(payload?.protocolSlot);
      const isSecondaryProtocol = payload?.isSecondaryProtocol === true;
      let shouldApplyProtocolSlot = false;

      if (protocolSlot) {
        const status = weekSlotStatus.get(protocolSlot) || {
          hasPrimary: false,
          hasSecondary: false,
        };
        if (isSecondaryProtocol) {
          shouldApplyProtocolSlot = status.hasPrimary && !status.hasSecondary;
        } else {
          shouldApplyProtocolSlot = !status.hasPrimary;
        }
      }

      if (protocolSlot && shouldApplyProtocolSlot) {
        newExercise.setProperty("protocolSlot", protocolSlot);
        newExercise.setProperty("isSecondaryProtocol", isSecondaryProtocol);
        recordWeekSlot(protocolSlot, isSecondaryProtocol);
      } else {
        newExercise.setProperty("protocolSlot", null);
        newExercise.setProperty("isSecondaryProtocol", null);
      }

      // Always copy protocolText (even if protocolSlot is empty).
      newExercise.setProperty(
        "protocolText",
        typeof payload?.protocolText === "string" ? payload.protocolText : ""
      );

      if (Array.isArray(payload?.series)) {
        payload.series.forEach((s, idx) => {
          if (!s) return;
          const newSerie = new Serie(
            uuidv4(),
            typeof s?.order === "number" ? s.order : idx,
            undefined,
            undefined,
            undefined,
            s?.targetReps ?? "",
            s?.targetRpe ?? "",
            s?.targetWeight ?? "",
            newExercise,
            typeof s?.relevance === "number" ? s.relevance : 3,
            undefined,
            !!s?.requireVideo
          );
          newExercise.series.push(newSerie);
        });
      }

      if (Array.isArray(payload?.attributes)) {
        payload.attributes.forEach((a, idx) => {
          if (!a) return;
          const newAttribute = new Attribute(
            uuidv4(),
            typeof a?.name === "string" ? a.name : "",
            typeof a?.title === "string" ? a.title : "",
            typeof a?.subtitle === "string" ? a.subtitle : "",
            typeof a?.order === "number" ? a.order : idx,
            false,
            false,
            null,
            newExercise
          );
          newExercise.attributes.push(newAttribute);
        });
      }

      this.exercises.push(newExercise);
      createdCount += 1;
    });

    this.setProperty("hasChanged", true);
    return createdCount;
  }
  // #endregion

  // #region /* HELPERS */
  get allWorkoutSeries() {
    const result = this.exercises.reduce((acc, exercise) => {
      exercise.series.forEach((serie) => {
        acc.push(serie);
      });
      return acc;
    }, []);

    return result;
  }

  setIsSelected(value) {
    this.isSelected = value;
  }

  // deleteWorkout() {
  //   // First, copy each exercise to block protocols
  //   this.exercises.forEach(e => e.copyExerciseToBlockProtocols());

  //   // Then remove this workout from its parent block
  //   if (this.block) {
  //     const idx = this.block.workouts.findIndex(w => w.id === this.id);
  //     if (idx > -1) {
  //       this.block.workouts.splice(idx, 1);
  //     }
  //   }else if (this.coach){
  //      const idx = this.coach.workoutLibrary.findIndex(w => w.id === this.id);
  //     if (idx > -1) {
  //       this.coach.workoutLibrary.splice(idx, 1);
  //     }
  //   }

  //    // Persist changes depending on client or coach context
  //     // if (this.block.client) {
  //     //   this.block.client.updateClientTransactionData(this.block.id);
  //     //   this.block.client.startOrResetBatchTimer();
  //     // } else if (this.block.coach) {
  //     //   this.block.coach.updateCoachTransactionBlocks(this.block.id.toLowerCase());
  //     //   this.block.coach.startOrResetBatchTimer();
  //     // }
  // }

  setProperty(propertyName, value) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      this[propertyName] = value;
    } else {
      console.error(`Property ${propertyName} does not exist.`);
    }
  }

  getProperty(propertyName) {
    if (Object.hasOwnProperty.call(this, propertyName)) {
      return this[propertyName];
    } else {
      console.error(`Property ${propertyName} does not exist.`);
      return undefined;
    }
  }
  // #endregion
}
export default Workout;
